import React, { useCallback } from 'react';
import { CloseIcon } from '@chakra-ui/icons';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogOverlay,
  Box,
  Button,
  Card,
  Flex,
  FormControl,
  FormLabel,
  GridItem,
  Icon,
  IconButton,
  Input,
  SimpleGrid,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import CommonInput from 'components/commonInput/CommonInput';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaPlus } from 'react-icons/fa';
import { MdLinkOff } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useColorModeVariables } from '../../../components/colorModeVarialbels/ColorModeValues';
import CommonSubmitBtn from '../../../components/commonInput/CommonSubmitBtn';
import {
  AddStationData,
  EditStationData,
  UnassignStationData,
  ViewStationData,
} from '../../../reactRedux/thunks/stationThunk/stationThunk';
import { stationAddEditValidation } from '../../../utils/CommonValidation';
import MerchantListModal from './MerchantListModal';
import { responseStationView } from 'reactRedux/slices/stationSlice/stationSlice';
import MapView from 'components/detailMapData/MapView';

const StationAddEdit = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isUnassignOpen,
    onOpen: onUnassignOpen,
    onClose: onUnassignClose,
  } = useDisclosure();
  const { textColor, brandStars } = useColorModeVariables();
  const { editStationId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const [page, setPage] = useState(
    Number(localStorage.getItem('currentPage')) || 1,
  );
  const [selectedMerchants, setSelectedMerchants] = useState('');
  const [selectedMerchantName, setSelectedMerchantName] = useState('');
  const [isRemove, setIsRemove] = useState(false);
  const { isLoading, viewStationDetails } = useSelector(
    (state) => state.StationSlice,
  );
  const {
    handleSubmit,
    setValue,
    register,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(stationAddEditValidation),
  });

  const onSubmit = (data) => {
    const { stationId, ...restData } = data;
    const transformedData = {
      ...restData,
      merchantId: selectedMerchants,
    };

    if (editStationId) {
      const editPayload = {
        location: data?.location,
      };
      dispatch(
        EditStationData(editStationId, editPayload, () => {
          navigate('/admin/stations');
          setPage(Number(localStorage.getItem('currentPage')) || 1);
        }),
      );
    } else {
      // Exclude coordinates when adding a new station
      const addStationData = { ...transformedData, stationId };
      delete addStationData.coordinates; // Ensure coordinates is not present
      setPage(localStorage.setItem('currentPage', 1) || 1);
      dispatch(
        AddStationData(addStationData, () => {
          navigate('/admin/stations');
        }),
      );
    }
  };

  useEffect(() => {
    if (editStationId) {
      dispatch(ViewStationData({ id: editStationId }));
    }
  }, [dispatch, editStationId]);

  useEffect(() => {
    if (viewStationDetails && editStationId) {
      setValue('stationId', viewStationDetails?.stationId);
      setValue('slots', viewStationDetails?.slots);
      setValue('location', viewStationDetails?.location);
      setSelectedMerchants(viewStationDetails?.merchant?.merchantId || '');
      setSelectedMerchantName(viewStationDetails?.merchant?.name || '');
    }
  }, [viewStationDetails, editStationId, setValue]);

  const handleRemoveMerchant = () => {
    setSelectedMerchants('');
    setIsRemove(true);
  };

  const handleUnassign = () => {
    onUnassignClose();
    dispatch(
      UnassignStationData(editStationId, () => {
        dispatch(ViewStationData({ id: editStationId }));
      }),
    );
  };

  const resetViewStationDetails = useCallback(() => {
    dispatch(responseStationView({ viewStationDetails: null }));
  }, [dispatch]);

  useEffect(() => {
    return () => {
      resetViewStationDetails();
    };
  }, [resetViewStationDetails]);

  return (
    <>
      <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
        <Flex direction="column" align="center" justify="center">
          <Card
            w="100%"
            boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
            borderRadius="20px"
            paddingInline="40px"
            paddingBlock="50px"
            overflowX={{ sm: 'scroll', lg: 'hidden' }}
          >
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <FormControl>
                <SimpleGrid columns={{ base: 1, md: 3, lg: 3 }} spacing="24px">
                  {/* station ID */}
                  <CommonInput
                    type="text"
                    name="stationId"
                    labelText="Station Id"
                    textColor={textColor}
                    brandStars={brandStars}
                    placeholder="Station Id"
                    register={register('stationId')}
                    errors={errors?.stationId}
                    readOnly={!!editStationId}
                    isEmailReadOnly={true}
                  />

                  {/* terminal ID */}
                  <CommonInput
                    type="number"
                    name="slots"
                    labelText="Slots"
                    textColor={textColor}
                    brandStars={brandStars}
                    placeholder="Slots"
                    register={register('slots')}
                    errors={errors?.slots}
                    readOnly={!!editStationId}
                    isEmailReadOnly={true}
                  />

                  <CommonInput
                    type="text"
                    name="location"
                    labelText="Label"
                    textColor={textColor}
                    brandStars={brandStars}
                    placeholder="Location"
                    register={register('location')}
                    isValidate={false}
                  />

                  <GridItem colSpan={{ base: 1, lg: 3 }}>
                    {editStationId &&
                      viewStationDetails?.merchant?.merchantId && (
                        <MapView
                          coordinates={
                            viewStationDetails?.coordinates || [0, 0]
                          }
                          isChange={false}
                          editStationId={editStationId}
                        />
                      )}
                  </GridItem>
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="24px">
                  {!selectedMerchants ? (
                    <Button
                      type="button"
                      bg="reviveOrange"
                      mt="50px"
                      h="50"
                      color="primary"
                      borderRadius="12px"
                      _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
                      onClick={onOpen}
                    >
                      <Icon
                        as={FaPlus}
                        width="15px"
                        height="15px"
                        borderRadius="12px"
                        marginRight="5px"
                        color="inherit"
                      />
                      Add Merchant
                    </Button>
                  ) : (
                    <Flex align="center" mt={2} marginTop={'50px'}>
                      <FormLabel flex={'none'} color={textColor}>
                        Merchant
                      </FormLabel>
                      <Input
                        value={
                          selectedMerchantName
                            ? selectedMerchantName
                            : selectedMerchants
                        }
                        readOnly
                        variant="auth"
                        fontSize="sm"
                        mb="2px"
                        fontWeight="500"
                        size="lg"
                        sx={{
                          backgroundColor: '#f5f5f5',
                          color: '#888',
                          border: '1px solid #ddd',
                          cursor: 'not-allowed',
                        }}
                      />
                      {!editStationId && (
                        <IconButton
                          aria-label="Remove Merchant"
                          icon={<CloseIcon />}
                          size="sm"
                          ms={3}
                          onClick={handleRemoveMerchant}
                        />
                      )}
                    </Flex>
                  )}
                </SimpleGrid>

                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing="24px">
                  <CommonSubmitBtn BTNtext="Submit" isLoading={isLoading} />
                  {editStationId && selectedMerchants && (
                    <Button
                      type="button"
                      mt="24px"
                      h="50"
                      bg="transparent"
                      color="red.500"
                      border="2px solid"
                      borderColor="red.400"
                      borderRadius="12px"
                      fontWeight="600"
                      leftIcon={<MdLinkOff />}
                      _hover={{ bg: 'red.50', borderColor: 'red.500' }}
                      _active={{ bg: 'red.100' }}
                      isLoading={isLoading}
                      onClick={onUnassignOpen}
                    >
                      Unassign Merchant
                    </Button>
                  )}
                </SimpleGrid>
                <MerchantListModal
                  isOpen={isOpen}
                  onClose={onClose}
                  setSelectedMerchants={setSelectedMerchants}
                  setIsRemove={setIsRemove}
                  isRemove={isRemove}
                  setSelectedMerchantName={setSelectedMerchantName}
                  setValue={setValue}
                />

                <AlertDialog
                  isOpen={isUnassignOpen}
                  onClose={onUnassignClose}
                  isCentered
                >
                  <AlertDialogOverlay>
                    <AlertDialogContent
                      maxWidth="400px"
                      borderRadius="lg"
                      boxShadow="lg"
                      p={4}
                    >
                      <AlertDialogBody
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        py={6}
                      >
                        <Box as={MdLinkOff} size={64} color="red.400" mb={4} />
                        <Text
                          fontSize="18px"
                          fontWeight="medium"
                          textAlign="center"
                          mt={3}
                        >
                          Are you sure you want to unassign this station from{' '}
                          <strong>{selectedMerchantName}</strong>?
                        </Text>
                        <Text
                          fontSize="13px"
                          color="gray.500"
                          textAlign="center"
                          mt={2}
                        >
                          The station will be reassigned to the default merchant
                          for its region.
                        </Text>
                      </AlertDialogBody>
                      <AlertDialogFooter justifyContent="center" gap={3}>
                        <Button
                          variant="outline"
                          onClick={onUnassignClose}
                          px={6}
                        >
                          Cancel
                        </Button>
                        <Button
                          bg="red.500"
                          color="white"
                          _hover={{ bg: 'red.600' }}
                          onClick={handleUnassign}
                          px={6}
                          isLoading={isLoading}
                        >
                          Yes, Unassign
                        </Button>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialogOverlay>
                </AlertDialog>
              </FormControl>
            </form>
          </Card>
        </Flex>
      </Box>
    </>
  );
};

export default StationAddEdit;
